import RiskTag from '@/components/increased-risk-tag/increased-risk-tag';
import { RiskFactor } from '@/repositories/risk.repository';
import { Box, Divider, Flex, FlexProps, Text } from '@cmpsr/components';
import RiskFactorCard from './components/risk-factor-card/risk-factor-card';

type RiskComparisonProps = FlexProps & {
  factorsIncreasingRisk: RiskFactor[];
  factorsDecreasingRisk: RiskFactor[];
  totalIncreasingFactors: number;
  totalDecreasingFactors: number;
  onOpenModal: (factor: RiskFactor) => void;
};

export default function RiskComparison({
  factorsIncreasingRisk,
  factorsDecreasingRisk,
  totalIncreasingFactors,
  totalDecreasingFactors,
  onOpenModal,
  ...props
}: RiskComparisonProps) {
  const RelativeToPopulationAverage = () => (
    <Text as="span" textStyle="text-header-s-v">
      relative to population average
    </Text>
  );

  const EmptyStateIncreasingRisk = () => {
    return (
      <Flex direction="column" gap="2">
        <RiskTag risk={0} alignSelf="flex-start" px={3} mb={4} arrowDirection="up" />
        <Text variant="text-header-S">
          No factors increasing your risk <RelativeToPopulationAverage />
        </Text>
        <Divider display={{ base: 'none', md: 'block' }} borderColor="ui-element-divider" />
      </Flex>
    );
  };

  const EmptyStateDecreasingRisk = () => {
    return (
      <Flex direction="column" gap="2">
        <RiskTag risk={0} alignSelf="flex-start" px={3} mb={4} arrowDirection="down" />
        <Text variant="text-header-S">
          No factors decreasing your risk <RelativeToPopulationAverage />
        </Text>
        <Divider display={{ base: 'none', md: 'block' }} borderColor="ui-element-divider" />
      </Flex>
    );
  };

  return (
    <Flex direction={{ base: 'column', md: 'row' }} gap="4" {...props}>
      <Box flexBasis="50%">
        <Flex
          height="100%"
          direction="column"
          border="1px solid"
          borderTopRadius="radii-3"
          borderBottomRadius="radii-3"
          borderColor="ui-element-divider"
          bgColor="background-card"
          p="6"
        >
          {totalIncreasingFactors > 0 ? (
            <>
              <RiskTag risk={totalIncreasingFactors} alignSelf="flex-start" px={3} mb={4} arrowDirection="up" />
              <Text variant="text-header-S" mb="6">
                Top factors increasing your risk <RelativeToPopulationAverage />
              </Text>
              <Divider mb="4" borderColor="ui-element-divider" />
              <Flex flexDir="column" gap="2">
                {factorsIncreasingRisk.map((factor, index) => (
                  <RiskFactorCard key={index} factor={factor} onClick={() => onOpenModal(factor)} />
                ))}
              </Flex>
            </>
          ) : (
            <EmptyStateIncreasingRisk />
          )}
        </Flex>
      </Box>
      <Box flexBasis="50%">
        <Flex
          height="100%"
          direction="column"
          border="1px solid"
          borderTopRadius="radii-3"
          borderBottomRadius="radii-3"
          borderColor="ui-element-divider"
          bgColor="background-card"
          p="6"
        >
          {totalDecreasingFactors > 0 ? (
            <>
              <RiskTag risk={totalDecreasingFactors} alignSelf="flex-start" px={3} mb={4} arrowDirection="down" />
              <Text variant="text-header-S" mb="6">
                Top factors decreasing your risk <RelativeToPopulationAverage />
              </Text>
              <Divider mb="4" borderColor="ui-element-divider" />
              <Flex flexDir="column" gap="2">
                {factorsDecreasingRisk.map((factor, index) => (
                  <RiskFactorCard key={index} factor={factor} onClick={() => onOpenModal(factor)} />
                ))}
              </Flex>
            </>
          ) : (
            <EmptyStateDecreasingRisk />
          )}
        </Flex>
      </Box>
    </Flex>
  );
}
